<script>
import devData from './dev-data/weekly_sales_per_group_per_customer.json'
import api from '@/helpers/api'
import { sumBy, chain, meanBy } from 'lodash'
import moment from 'moment'
import toCurrency from '@/helpers/currency'

export default {
  name: 'period-sales-per-customer',
  data () {
    return {
      selectedCustomer: null,
      periods: {
        isoWeek: {
          title: 'week',
          type: 'isoWeek',
          format: '[Week #]w [-] DD/MM/yyyy',
          label: 'weekly'
        },
        month: {
          title: 'month',
          type: 'month',
          format: 'MMMM yyyy',
          label: 'monthly'
        },
        year: { title: 'year', type: 'year', format: 'yyyy', label: 'yearly' }
      },
      showTable: true,
      selected_period: {},
      isLoading: false,
      start_date: moment(),
      end_date: moment(),
      total: 0,
      data: [],
      fields: [
        {
          key: 'customer',
          label: 'Company Name'
        },
        {
          key: 'value',
          label: 'Sales',
          thStyle: { width: '110px' },
          class: 'text-right'
        }
      ],
      customerFields: [
        {
          key: 'group',
          label: 'Category'
        },
        {
          key: 'value',
          label: 'Sales',
          thStyle: { width: '110px' },
          class: 'text-right'
        }
      ],
      options: {},
      series: [44, 55, 41, 17, 15]
    }
  },
  watch: {
    selected_period: 'initPeriods',
    start_date: 'onRefresh'
  },
  mounted () {
    this.selected_period = this.periods.year
  },
  methods: {
    initPeriods () {
      this.start_date = moment().startOf(this.selected_period.type)
      this.end_date = moment(this.start_date).endOf(this.selected_period.type)
    },
    selectCustomer (customer) {
      this.selectedCustomer = customer
      this.onRefresh()
    },
    goBack () {
      this.selectedCustomer = null
      this.onRefresh()
    },
    async onRefresh () {
      this.isLoading = true

      var result = []
      if (process.env.NODE_ENV !== 'production') {
        result = chain(devData)
          .filter(
            x =>
              moment(x.start_date).isSameOrAfter(moment(this.start_date)) &&
              moment(x.end_date).isSameOrBefore(moment(this.end_date))
          )
          .value()
      } else {
        var start_date = moment(this.start_date).format('MM/DD/YYYY')
        var end_date = moment(this.end_date).format('MM/DD/YYYY')

        result = await api.stats.weeklySalesPerItemGroupPerCustomer({
          start_date,
          end_date
        })
      }

      if (this.selectedCustomer !== null) {
        result = result.filter(
          x => x.customer_account_id === this.selectedCustomer.id
        )
        this.data = chain(result)
          .groupBy(x => x.top_item_group.description)
          .map((d, c) => ({
            group: c,
            value: sumBy(d, 'total_sales')
          }))
          .orderBy('value', 'desc')
          .value()
      } else {
        this.data = chain(result)
          .groupBy(x => x.customer_account_id)
          .map((d, c) => ({
            customer: { ...d[0].customer, id: d[0].customer_account_id },
            value: sumBy(d, 'total_sales')
          }))
          .orderBy('value', 'desc')
          .value()
      }

      this.total = sumBy(this.data, 'value')

      this.series = this.data.map(x => x.value)
      this.options = {
        ...this.options,
        labels: this.data.map(x => x.customer),
        legend: {
          position: 'right',
          width: '50px',
          floating: true,
          show: false
        },
        tooltip: {
          y: {
            formatter: function (val, opts) {
              return toCurrency(val.toFixed(2))
            }
          }
        }
      }

      this.isLoading = false
    },
    nextPeriod () {
      if (this.nextDisabled) return
      this.start_date = moment(this.start_date).add(
        1,
        this.selected_period.type
      )
      this.end_date = moment(this.start_date).endOf(this.selected_period.type)
    },
    prevPeriod () {
      if (this.prevDisabled) return
      this.start_date = moment(this.start_date).subtract(
        1,
        this.selected_period.type
      )
      this.end_date = moment(this.start_date).endOf(this.selected_period.type)
    }
  },
  computed: {
    currentPeriod () {
      var d = moment(this.start_date).format(this.selected_period.format)
      return d
    },
    nextDisabled () {
      return moment(this.end_date).isAfter(moment())
    },
    prevDisabled () {
      return moment(this.start_date).isBefore(moment('01/01/2017'))
    }
  }
}
</script>
<template>
  <b-card no-body>
    <b-card-body class="p-3">
      <div class="float-end">
        <b-btn-group class="m-0">
          <b-btn
            size="sm"
            variant="link"
            @click="prevPeriod"
            :disabled="prevDisabled"
          >
            <i class="bx bx bx-chevrons-left" />
          </b-btn>
          <b-btn
            size="sm"
            :variant="selected_period !== period ? 'link' : 'primary'"
            v-for="period in periods"
            :key="period.title"
            @click="selected_period = period"
            >{{ period.title }}</b-btn
          >
          <b-btn
            size="sm"
            variant="link"
            @click="nextPeriod"
            :disabled="nextDisabled"
          >
            <i class="bx bx bx-chevrons-right" />
          </b-btn>
        </b-btn-group>
        <b-btn-group class="m-0 ml-2">
          <b-btn
            size="sm"
            :variant="!showTable ? 'link' : 'primary'"
            class="px-2 py-1"
            @click="showTable = true"
          >
            <i class="bx bx-list-ol font-size-20" />
          </b-btn>
          <b-btn
            size="sm"
            :variant="showTable ? 'link' : 'primary'"
            @click="showTable = false"
            class="px-2 py-1"
          >
            <i class="bx bx-pie-chart-alt font-size-20 m-0 p-0" />
          </b-btn>
        </b-btn-group>
      </div>
      <div>
        <h4 class="mb-1 mt-0">{{ currentPeriod }}</h4>
        <p
          class="text-muted mb-0 d-flex w-100 align-items-center justify-content-between"
        >
          <template v-if="selectedCustomer">
            <span class="mr-2">
              {{ $t('Sales') }} {{ selectedCustomer.company_name }}
            </span>
            <b-btn @click="goBack" variant="link" class="p-0 font-size-xs">{{
              $t('GO BACK')
            }}</b-btn>
          </template>
          <template v-else>
            {{ $t('Customer Sales') }}
          </template>
        </p>
      </div>

      <b-table
        v-show="showTable && selectedCustomer === null"
        show-empty
        hover
        striped
        align-middle
        :items="data"
        class="table-sm mt-2 hidden-scrollbar"
        :fields="fields"
        :busy="isLoading"
        head-variant="light"
        tbody-tr-class="pointer"
        no-border-collapse
        sticky-header="300px"
      >
        <template v-slot:cell(customer)="row">
          <a href="#" @click="selectCustomer(row.value)">{{
            row.value.company_name
          }}</a>
        </template>
        <template v-slot:cell(value)="row">
          {{ row.value | toCurrency }}
        </template>
      </b-table>

      <b-table
        v-show="showTable && selectedCustomer"
        show-empty
        hover
        striped
        align-middle
        :items="data"
        class="table-sm mt-2 hidden-scrollbar"
        :fields="customerFields"
        :busy="isLoading"
        head-variant="light"
        tbody-tr-class="pointer"
        no-border-collapse
        sticky-header="300px"
      >
        <template v-slot:cell(group)="row">
          {{ row.value }}
        </template>
        <template v-slot:cell(value)="row">
          {{ row.value | toCurrency }}
        </template>
      </b-table>

      <apexchart
        class="p-4 d-flex justify-content-center"
        width="450px"
        v-if="!showTable"
        type="donut"
        :options="options"
        :series="series"
      ></apexchart>

      <div class="float-end font-weight-bold">
        {{ total | toCurrency }}
      </div>

      <b-table-simple hover small caption-top sticky-footer v-if="false">
        <b-thead head-variant="light">
          <b-tr>
            <b-th>{{ $t('Customer') }}</b-th>
            <b-th class="text-right">{{ $t('Sales') }}</b-th>
          </b-tr>
        </b-thead>
        <b-tbody style="height: 300px">
          <b-tr v-for="row in data" :key="row.customer">
            <b-td stacked-heading="Customer" class="text-left"> </b-td>
            <b-td stacked-heading="Sales" class="text-right">{{
              row.value | toCurrency
            }}</b-td>
          </b-tr>
        </b-tbody>
        <b-tfoot>
          <b-tr>
            <b-th colspan="2" variant="light" class="text-right py-2">
              {{ total | toCurrency }}
            </b-th>
          </b-tr>
        </b-tfoot>
      </b-table-simple>

      <span class="font-size-xs">
        {{ start_date | moment('DD/MM/YYYY') }} -
        {{ end_date | moment('DD/MM/YYYY') }}
      </span>
    </b-card-body>
  </b-card>
</template>
